import { useEffect, useRef, useState } from "react";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";

/**
 * isLoading 變 false 後延遲多久才真的隱藏。
 * 全站多處各自呼叫 setLoading(true/false)，請求前後接連時常見 true→false→true；
 * 短暫 false 用延遲合併，避免 overlay 閃一下又出現。
 *
 * 已顯示超過 SHORT_SHOW_MS（例如 LIFF 登入）則立刻隱藏，避免與頁內 spinner（kiosk）疊加。
 */
const HIDE_DELAY_MS = 800;
const SHORT_SHOW_MS = 500;

/**
 * 全域 loading：改為低調半透明 + 小轉圈。
 * 先前全螢幕鋼瓶品牌頁太搶眼，登入→首頁若有接縫會像「連跳兩次」；
 * 一般 spinner 即使偶發開關也不那麼刺眼。
 */
const Loading = () => {
  const { isLoading } = useUtilsStore();
  const [visible, setVisible] = useState(isLoading);
  const visibleRef = useRef(isLoading);
  const shownSinceRef = useRef(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }

    if (isLoading) {
      if (!visibleRef.current) {
        shownSinceRef.current = Date.now();
      }
      visibleRef.current = true;
      setVisible(true);
      return;
    }

    const shownFor = shownSinceRef.current > 0 ? Date.now() - shownSinceRef.current : 0;
    const delay = shownFor > 0 && shownFor < SHORT_SHOW_MS ? HIDE_DELAY_MS : 0;

    const hide = () => {
      visibleRef.current = false;
      setVisible(false);
      shownSinceRef.current = 0;
      hideTimer.current = null;
    };

    if (delay <= 0) {
      hide();
      return;
    }

    hideTimer.current = setTimeout(hide, delay);

    return () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };
  }, [isLoading]);

  if (!visible) return null;

  return (
    <div className="wrapper-loading" role="status" aria-live="polite" aria-label="載入中">
      <style>{`
        .simple-load-page {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: rgba(248, 250, 252, 0.96);
          backdrop-filter: blur(1.5px);
          -webkit-backdrop-filter: blur(1.5px);
        }
        .simple-load-spinner {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 2.5px solid rgba(59, 130, 246, 0.22);
          border-top-color: #3B82F6;
          animation: simpleLoadSpin 0.7s linear infinite;
        }
        .simple-load-label {
          color: #64748b;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.04em;
        }
        @keyframes simpleLoadSpin {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .simple-load-spinner {
            animation: none;
            border-top-color: #3B82F6;
            border-right-color: rgba(59, 130, 246, 0.45);
          }
        }
      `}</style>
      <div className="simple-load-page">
        <div className="simple-load-spinner" aria-hidden />
        <div className="simple-load-label">載入中</div>
      </div>
    </div>
  );
};

export default Loading;
